import {useEffect, useState} from "react";
import {Button} from "../../../basic/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {UserPlus} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {useCreateUserMutation, useGetTeamByIdQuery} from "../../../../store/generated/api";
import {UserForm} from "./UserForm";
import {RoleSelector} from "./RoleSelector";
import {TeamSelector} from "./TeamSelector";
import {getDisplayName} from "../../../../utils/nameUtils";

interface CreateUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateUserDialog({open, onOpenChange}: CreateUserDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [createUser, {isLoading}] = useCreateUserMutation();
    const [userData, setUserData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        role: "",
        teamId: ""
    });

    const [teamIdLookup, setTeamIdLookup] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setTeamIdLookup(userData.teamId.trim()), 500);
        return () => clearTimeout(timer);
    }, [userData.teamId]);

    const {data: teamData} = useGetTeamByIdQuery(
        {teamId: teamIdLookup},
        {skip: !teamIdLookup}
    );
    const isUniversityTeam = teamData?.isUniversity === true;

    useEffect(() => {
        if (userData.role === "Student" && !isUniversityTeam) {
            setUserData({...userData, role: ""});
        }
    }, [isUniversityTeam]);

    const handleCreateUser = async () => {
        if (!userData.email.trim()) {
            showErrorToast({message: t('admin.users.createUserDialog.errors.emailRequired')});
            return;
        }

        if (!userData.role) {
            showErrorToast({message: t('admin.users.createUserDialog.errors.roleRequired')});
            return;
        }

        if (!userData.teamId) {
            showErrorToast({message: t('admin.users.createUserDialog.errors.teamIdRequired')});
            return;
        }

        try {
            const roleMapping: { [key: string]: "ADMIN" | "USER" | "STUDENT" } = {
                "Admin": "ADMIN",
                "User": "USER",
                "Student": "STUDENT"
            };

            const mappedRole = roleMapping[userData.role];
            if (!mappedRole) {
                showErrorToast({message: t('admin.users.createUserDialog.errors.invalidRole')});
                return;
            }

            await createUser({
                vwpUserCreationRequest: {
                    email: userData.email.trim(),
                    firstName: userData.firstName.trim(),
                    lastName: userData.lastName.trim(),
                    teamId: userData.teamId,
                    role: mappedRole
                }
            }).unwrap();

            const fullName = getDisplayName({
                firstName: userData.firstName.trim(),
                lastName: userData.lastName.trim()
            });
            toast.success(t('admin.users.createUserDialog.success', {name: fullName, email: userData.email}));
            onOpenChange(false);
            setUserData({email: "", firstName: "", lastName: "", role: "", teamId: ""});
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.users.createUserDialog.errors.creationFailed')});
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[85vw] max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('admin.users.createUserDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.users.createUserDialog.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="grid md:grid-cols-2 gap-6">
                        <UserForm
                            email={userData.email}
                            firstName={userData.firstName}
                            lastName={userData.lastName}
                            onEmailChange={(email) => setUserData({...userData, email})}
                            onFirstNameChange={(firstName) => setUserData({...userData, firstName})}
                            onLastNameChange={(lastName) => setUserData({...userData, lastName})}
                        />

                        <RoleSelector
                            role={userData.role}
                            onRoleChange={(role) => setUserData({...userData, role})}
                            isUniversityTeam={isUniversityTeam}
                        />

                        <TeamSelector
                            teamId={userData.teamId}
                            onTeamIdChange={(teamId) => setUserData({...userData, teamId})}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleCreateUser} disabled={isLoading}>
                        <UserPlus className="h-4 w-4 mr-2"/>
                        {isLoading ? t('common.creating') : t('admin.users.createUserButton')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}